import { useMemo } from 'react';
import type { CurrentMemberFragment } from 'generated/graphql';
import { useMaybeAppData } from '@modules/root';
import { useRouteLoaderData } from '@remix-run/react';
import type { RootData } from '~/root';
import type { User } from '~/server/types';

/**
 * Check if a user is logged-in.
 * @param user The user to check.
 * @returns The user is logged-in.
 */
export function isUserLoggedIn(
    user: User | null | undefined,
): user is CurrentMemberFragment {
    return user?.__typename === 'Me';
}

/**
 * Hook to get the current user.
 * @returns The current user.
 */
export function useUser(): User {
    const appData = useMaybeAppData();
    const rootData = useRouteLoaderData('root') as RootData | undefined;

    if (appData) {
        return appData.user;
    }
    if (rootData?.user) {
        return rootData.user;
    }
    throw new Error('useUser: missing user on routes/_app and root loaders');
}

/**
 * Hook to get the current user and if they are logged-in.
 * @returns The user and the user logged-in state.
 */
export function useUserLoggedIn() {
    const user = useUser();
    return useMemo(() => {
        if (isUserLoggedIn(user)) {
            return { user, isLoggedIn: true } as const;
        }
        return { user, isLoggedIn: false } as const;
    }, [user]);
}
